import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Table,
  Tooltip,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ArrowBackIos, ArrowForwardIos, Schedule, Receipt, VerifiedUser, Science } from '@mui/icons-material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import { useMultiStepForm } from 'src/shared/hooks/useMultiStepForm';
import { StyledMultiStepConnector } from 'src/shared/components/multistepper/styledMultiStepConnector';
import { FortnoxValidationResult, SendTestVoucherResponse, VoucherPreviewResponse } from 'src/shared/services/accountingIntegrationService';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import { useState } from 'react';

type AccountingFrequency = 'off' | 'daily' | 'monthly';

type FortnoxSettingsModalProps = {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  onDisconnect: () => void;
  pendingFrequency: AccountingFrequency;
  setPendingFrequency: (f: AccountingFrequency) => void;
  hasExistingTask: boolean;
  nextRunDisplay: string;
  startDateTime: Dayjs | null;
  setStartDateTime: (d: Dayjs | null) => void;
  filterByDate: boolean;
  setFilterByDate: (v: boolean) => void;
  filterFromDate: Dayjs | null;
  setFilterFromDate: (d: Dayjs | null) => void;
  voucherGrouping: 'daily' | 'monthly';
  setVoucherGrouping: (v: 'daily' | 'monthly') => void;
  validationData: FortnoxValidationResult | null;
  validationLoading: boolean;
  runValidation: () => void;
  previewDate: Dayjs | null;
  setPreviewDate: (d: Dayjs | null) => void;
  previewData: VoucherPreviewResponse | null;
  previewLoading: boolean;
  handlePreview: (date?: string) => void;
  handleSendTest: (date?: string) => void;
  sendTestLoading: boolean;
  sendTestResult: SendTestVoucherResponse | null;
};

const StepInterval = ({
  pendingFrequency,
  setPendingFrequency,
  hasExistingTask,
  nextRunDisplay,
  startDateTime,
  setStartDateTime,
  filterByDate,
  setFilterByDate,
  filterFromDate,
  setFilterFromDate,
}: Pick<
  FortnoxSettingsModalProps,
  | 'pendingFrequency'
  | 'setPendingFrequency'
  | 'hasExistingTask'
  | 'nextRunDisplay'
  | 'startDateTime'
  | 'setStartDateTime'
  | 'filterByDate'
  | 'setFilterByDate'
  | 'filterFromDate'
  | 'setFilterFromDate'
>) => {
  const { t } = useTranslation(['invoice']);

  const renderStartPicker = () => {
    if (hasExistingTask && nextRunDisplay) {
      return (
        <Typography variant='body2' color='text.secondary'>
          {t('invoice:accountingIntegration.nextRun')}: {nextRunDisplay}
        </Typography>
      );
    }
    return (
      <DateTimePicker
        label={t('invoice:accountingIntegration.startDateTime')}
        value={startDateTime}
        onChange={(val) => setStartDateTime(val)}
        ampm={false}
      />
    );
  };

  return (
    <Box>
      <Typography variant='h6' sx={{ fontWeight: 'bold', mb: 1 }}>
        {t('invoice:accountingIntegration.frequencyTitle')}
      </Typography>
      <FormControl>
        <RadioGroup value={pendingFrequency} onChange={(e) => setPendingFrequency(e.target.value as AccountingFrequency)}>
          <FormControlLabel value='off' control={<Radio />} label={t('invoice:accountingIntegration.frequencyOff')} />

          <FormControlLabel value='daily' control={<Radio />} label={t('invoice:accountingIntegration.frequencyDaily')} />
          {pendingFrequency === 'daily' && <Box sx={{ ml: 4, mb: 1 }}>{renderStartPicker()}</Box>}

          <FormControlLabel value='monthly' control={<Radio />} label={t('invoice:accountingIntegration.frequencyMonthly')} />
          {pendingFrequency === 'monthly' && <Box sx={{ ml: 4, mb: 1 }}>{renderStartPicker()}</Box>}
        </RadioGroup>
      </FormControl>

      {pendingFrequency !== 'off' && (
        <Box sx={{ mt: 2 }}>
          <Alert severity='info' sx={{ mb: 2 }}>
            <Typography variant='body2'>{t('invoice:accountingIntegration.pendingPaymentsInfo')}</Typography>
          </Alert>
          <FormControlLabel
            control={<Checkbox checked={filterByDate} onChange={(e) => setFilterByDate(e.target.checked)} />}
            label={t('invoice:accountingIntegration.filterByDateLabel')}
          />
          {filterByDate && (
            <Box sx={{ mt: 1, ml: 4 }}>
              <DatePicker
                label={t('invoice:accountingIntegration.filterFromDateLabel')}
                value={filterFromDate}
                onChange={(val) => setFilterFromDate(val)}
                slotProps={{ field: { clearable: true } }}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

const StepVoucher = ({
  voucherGrouping,
  setVoucherGrouping,
  pendingFrequency,
}: Pick<FortnoxSettingsModalProps, 'voucherGrouping' | 'setVoucherGrouping' | 'pendingFrequency'>) => {
  const { t } = useTranslation(['invoice']);
  const isDaily = pendingFrequency === 'daily';

  return (
    <Box>
      <Typography variant='h6' sx={{ fontWeight: 'bold', mb: 1 }}>
        {t('invoice:accountingIntegration.voucherGroupingTitle')}
      </Typography>
      <FormControl>
        <RadioGroup value={isDaily ? 'daily' : voucherGrouping} onChange={(e) => setVoucherGrouping(e.target.value as 'daily' | 'monthly')}>
          <FormControlLabel value='daily' control={<Radio />} label={t('invoice:accountingIntegration.voucherGroupingDaily')} />
          <Tooltip title={isDaily ? (t('invoice:accountingIntegration.voucherGroupingMonthlyDisabled') as string) : ''} arrow>
            <span>
              <FormControlLabel
                value='monthly'
                control={<Radio />}
                label={t('invoice:accountingIntegration.voucherGroupingMonthly')}
                disabled={isDaily}
              />
            </span>
          </Tooltip>
        </RadioGroup>
      </FormControl>
    </Box>
  );
};

const StepValidation = ({
  validationData,
  validationLoading,
  runValidation,
}: Pick<FortnoxSettingsModalProps, 'validationData' | 'validationLoading' | 'runValidation'>) => {
  const { t } = useTranslation(['invoice']);

  return (
    <Box>
      <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1 }}>
        <Typography variant='h6' sx={{ fontWeight: 'bold' }}>
          {t('invoice:accountingIntegration.validateTitle')}
        </Typography>
        {!validationLoading && validationData && (
          <Button variant='text' size='small' onClick={runValidation}>
            {t('invoice:accountingIntegration.validateRefresh')}
          </Button>
        )}
      </Stack>

      {validationLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1 }}>
          <CircularProgress size={18} />
          <Typography variant='body2' color='text.secondary'>
            {t('invoice:accountingIntegration.validating')}
          </Typography>
        </Box>
      )}

      {validationData && (
        <Stack spacing={0.5}>
          {validationData.companyName && (
            <Typography variant='body2' sx={{ fontWeight: 'bold', mb: 0.5 }}>
              {t('invoice:accountingIntegration.validateCompany')}: {validationData.companyName}
            </Typography>
          )}

          {validationData.accounts.map((acc) => (
            <Box key={acc.accountNumber} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {acc.exists && acc.active ? (
                <CheckCircleOutlineIcon color='success' sx={{ fontSize: 18 }} />
              ) : (
                <ErrorOutlineIcon color='error' sx={{ fontSize: 18 }} />
              )}
              <Typography variant='body2'>
                {t('invoice:accountingIntegration.validateAccount')} {acc.accountNumber}
                {acc.exists && acc.accountName ? ` (${acc.accountName})` : ''}
                {!acc.exists ? ` — ${t('invoice:accountingIntegration.validateNotFound')}` : ''}
                {acc.exists && !acc.active ? ` — ${t('invoice:accountingIntegration.validateNotActive')}` : ''}
              </Typography>
            </Box>
          ))}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {validationData.voucherSeries?.valid ? (
              <CheckCircleOutlineIcon color='success' sx={{ fontSize: 18 }} />
            ) : (
              <ErrorOutlineIcon color='error' sx={{ fontSize: 18 }} />
            )}
            <Typography variant='body2'>
              {t('invoice:accountingIntegration.validateVoucherSeries')} {validationData.voucherSeries?.value || '—'}
              {validationData.voucherSeries?.valid && validationData.voucherSeries?.description
                ? ` (${validationData.voucherSeries.description})`
                : ''}
              {!validationData.voucherSeries?.valid ? ` — ${t('invoice:accountingIntegration.validateNotFound')}` : ''}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {validationData.financialYear?.valid ? (
              <CheckCircleOutlineIcon color='success' sx={{ fontSize: 18 }} />
            ) : (
              <ErrorOutlineIcon color='error' sx={{ fontSize: 18 }} />
            )}
            <Typography variant='body2'>
              {t('invoice:accountingIntegration.validateFinancialYear')}
              {validationData.financialYear?.valid && validationData.financialYear?.description ? ` ${validationData.financialYear.description}` : ''}
              {!validationData.financialYear?.valid ? ` — ${t('invoice:accountingIntegration.validateNotFound')}` : ''}
            </Typography>
          </Box>
        </Stack>
      )}
    </Box>
  );
};

const StepTest = ({
  previewDate,
  setPreviewDate,
  previewData,
  previewLoading,
  handlePreview,
  handleSendTest,
  sendTestLoading,
  sendTestResult,
}: Pick<
  FortnoxSettingsModalProps,
  'previewDate' | 'setPreviewDate' | 'previewData' | 'previewLoading' | 'handlePreview' | 'handleSendTest' | 'sendTestLoading' | 'sendTestResult'
>) => {
  const { t } = useTranslation(['invoice']);
  const [filterPreviewByDate, setFilterPreviewByDate] = useState(false);

  return (
    <Box>
      <Typography variant='h6' sx={{ fontWeight: 'bold', mb: 1 }}>
        {t('invoice:accountingIntegration.previewTitle')}
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        {t('invoice:accountingIntegration.previewInfoText')}
      </Typography>
      <Stack spacing={1.5}>
        <FormControlLabel
          control={<Checkbox checked={filterPreviewByDate} onChange={(e) => setFilterPreviewByDate(e.target.checked)} />}
          label={t('invoice:accountingIntegration.previewFilterByDate')}
        />
        {filterPreviewByDate && (
          <DatePicker label={t('invoice:accountingIntegration.previewDateLabel')} value={previewDate} onChange={(val) => setPreviewDate(val)} />
        )}
        <Box>
          <Button
            variant='outlined'
            size='small'
            disableElevation
            onClick={() => handlePreview(filterPreviewByDate && previewDate ? previewDate.format('YYYYMMDD') : undefined)}
            disabled={previewLoading}
          >
            {t('invoice:accountingIntegration.previewButton')}
          </Button>
        </Box>
      </Stack>

      {previewLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {previewData && !previewData.hasPayments && (
        <Alert severity='info' sx={{ mt: 2 }}>
          {previewData.message}
        </Alert>
      )}

      {previewData?.vouchers &&
        previewData.vouchers.length > 0 &&
        (() => {
          const hasErrors = !!(
            (previewData.warnings && previewData.warnings.length > 0) ||
            previewData.vouchers?.some((v) => !v.hasFinancialYear || v.rows.some((r) => !r.account || !r.accountExists))
          );

          const sendButton = (
            <Button
              variant='contained'
              size='small'
              disableElevation
              onClick={() => handleSendTest(filterPreviewByDate && previewDate ? previewDate.format('YYYYMMDD') : undefined)}
              disabled={sendTestLoading || hasErrors}
            >
              {t('invoice:accountingIntegration.sendTestButton')}
            </Button>
          );

          return (
            <Box sx={{ mt: 2 }}>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                {t('invoice:accountingIntegration.previewTotalSummary', {
                  voucherCount: previewData.summary?.voucherCount,
                  paymentCount: previewData.summary?.paymentCount,
                  amount: previewData.summary?.totalAmount.toLocaleString('sv-SE'),
                })}
              </Typography>

              <Box sx={{ mb: 2 }}>
                {sendButton}
                {hasErrors && (
                  <Typography variant='caption' color='error' sx={{ ml: 1 }}>
                    {t('invoice:accountingIntegration.sendTestDisabledReason')}
                  </Typography>
                )}
              </Box>

              {previewData.warnings && previewData.warnings.length > 0 && (
                <Alert severity='warning' sx={{ mb: 2 }}>
                  {previewData.warnings.map((w) => (
                    <Typography key={w} variant='body2'>
                      {w}
                    </Typography>
                  ))}
                </Alert>
              )}

              {previewData.vouchers.map((voucher) => (
                <Box key={`${voucher.transactionDate}-${voucher.payoutId ?? 'payments'}`} sx={{ mb: 3 }}>
                  <Alert severity={voucher.hasFinancialYear ? 'success' : 'error'} icon={false} sx={{ mb: 1 }}>
                    <Typography variant='subtitle2'>
                      {voucher.description}
                      {!voucher.hasFinancialYear && ` — ${t('invoice:accountingIntegration.previewNoFinancialYear')}`}
                    </Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {t('invoice:accountingIntegration.previewSeries')}: {voucher.voucherSeries}
                      {' — '}
                      {voucher.payoutId
                        ? t('invoice:accountingIntegration.previewPayoutSummary', {
                            net: (voucher.payoutNetAmount ?? 0).toLocaleString('sv-SE'),
                            fee: (voucher.payoutFeeAmount ?? 0).toLocaleString('sv-SE'),
                            payoutId: voucher.payoutId,
                          })
                        : t('invoice:accountingIntegration.previewSummary', {
                            count: voucher.paymentCount,
                            amount: voucher.totalAmount.toLocaleString('sv-SE'),
                          })}
                    </Typography>
                  </Alert>
                  <Table size='small'>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('invoice:accountingIntegration.previewAccount')}</TableCell>
                        <TableCell>{t('invoice:accountingIntegration.previewDescription')}</TableCell>
                        <TableCell>{t('invoice:accountingIntegration.previewTransInfo')}</TableCell>
                        <TableCell align='right'>{t('invoice:accountingIntegration.previewDebit')}</TableCell>
                        <TableCell align='right'>{t('invoice:accountingIntegration.previewCredit')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {voucher.rows.map((row) => (
                        <TableRow
                          key={`${row.account}-${row.debit}-${row.credit}-${row.transactionInformation}`}
                          sx={!row.account || !row.accountExists ? { backgroundColor: 'error.light' } : undefined}
                        >
                          <TableCell>
                            {!row.account ? (
                              <Typography variant='body2' color='error'>
                                {t('invoice:accountingIntegration.previewAccountMissing')}
                              </Typography>
                            ) : (
                              <>
                                {row.account}
                                {row.accountName && ` ${row.accountName}`}
                                {!row.accountExists && (
                                  <Typography component='span' variant='body2' color='error'>
                                    {' '}
                                    — {t('invoice:accountingIntegration.previewAccountNotInFortnox')}
                                  </Typography>
                                )}
                              </>
                            )}
                          </TableCell>
                          <TableCell>{row.description}</TableCell>
                          <TableCell>{row.transactionInformation || ''}</TableCell>
                          <TableCell align='right'>{row.debit > 0 ? row.debit.toLocaleString('sv-SE') : ''}</TableCell>
                          <TableCell align='right'>{row.credit > 0 ? row.credit.toLocaleString('sv-SE') : ''}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              ))}

              <Box sx={{ mt: 2 }}>{sendButton}</Box>

              {sendTestLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant='body2' color='text.secondary'>
                    {t('invoice:accountingIntegration.sendTestLoading')}
                  </Typography>
                </Box>
              )}

              {sendTestResult && sendTestResult.success && (
                <Alert severity='success' sx={{ mt: 1 }}>
                  <Typography variant='body2'>
                    {t('invoice:accountingIntegration.sendTestSuccess', {
                      verificationNo: sendTestResult.verificationNo,
                      voucherCount: sendTestResult.voucherCount,
                      paymentCount: sendTestResult.paymentCount,
                    })}
                  </Typography>
                </Alert>
              )}

              {sendTestResult && !!sendTestResult.skippedReasons?.length && (
                <Alert severity='warning' sx={{ mt: 1 }}>
                  <Typography variant='body2'>{t('invoice:accountingIntegration.sendTestSkipped')}</Typography>
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {sendTestResult.skippedReasons.map((reason) => (
                      <li key={reason}>
                        <Typography variant='body2'>{reason}</Typography>
                      </li>
                    ))}
                  </ul>
                </Alert>
              )}

              {sendTestResult && !sendTestResult.success && (
                <Alert severity='error' sx={{ mt: 1 }}>
                  <Typography variant='body2'>{sendTestResult.error}</Typography>
                </Alert>
              )}
            </Box>
          );
        })()}
    </Box>
  );
};

const STEP_LABELS = ['stepInterval', 'stepVoucher', 'stepValidation', 'stepTest'];
const STEP_ICONS = [Schedule, Receipt, VerifiedUser, Science];

export const FortnoxSettingsModal = ({
  open,
  onClose,
  onSave,
  onDisconnect,
  pendingFrequency,
  setPendingFrequency,
  hasExistingTask,
  nextRunDisplay,
  startDateTime,
  setStartDateTime,
  filterByDate,
  setFilterByDate,
  filterFromDate,
  setFilterFromDate,
  voucherGrouping,
  setVoucherGrouping,
  validationData,
  validationLoading,
  runValidation,
  previewDate,
  setPreviewDate,
  previewData,
  previewLoading,
  handlePreview,
  handleSendTest,
  sendTestLoading,
  sendTestResult,
}: FortnoxSettingsModalProps) => {
  const { t } = useTranslation(['invoice', 'common']);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { step, currentStepIndex, isFirstStep, isLastStep, back, next, goTo } = useMultiStepForm([
    <StepInterval
      pendingFrequency={pendingFrequency}
      setPendingFrequency={setPendingFrequency}
      hasExistingTask={hasExistingTask}
      nextRunDisplay={nextRunDisplay}
      startDateTime={startDateTime}
      setStartDateTime={setStartDateTime}
      filterByDate={filterByDate}
      setFilterByDate={setFilterByDate}
      filterFromDate={filterFromDate}
      setFilterFromDate={setFilterFromDate}
    />,
    <StepVoucher voucherGrouping={voucherGrouping} setVoucherGrouping={setVoucherGrouping} pendingFrequency={pendingFrequency} />,
    <StepValidation validationData={validationData} validationLoading={validationLoading} runValidation={runValidation} />,
    <StepTest
      previewDate={previewDate}
      setPreviewDate={setPreviewDate}
      previewData={previewData}
      previewLoading={previewLoading}
      handlePreview={handlePreview}
      handleSendTest={handleSendTest}
      sendTestLoading={sendTestLoading}
      sendTestResult={sendTestResult}
    />,
  ]);

  return (
    <>
      <GenericDialog
        open={open}
        onClose={onClose}
        onSave={onSave}
        dialogTitle={t('invoice:accountingIntegration.settingsTitle') as string}
        maxWidth='md'
        padding={0}
        disableBackdropClosing
        dialogActions={
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button variant='outlined' color='error' size='small' onClick={() => setConfirmDisconnect(true)}>
              {t('invoice:accountingIntegration.disconnectButton')}
            </Button>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {!isFirstStep && (
                <Button variant='outlined' onClick={back} startIcon={<ArrowBackIos fontSize='small' />}>
                  {t('common:button.back')}
                </Button>
              )}
              {!isLastStep && (
                <Button variant='contained' disableElevation onClick={next} endIcon={<ArrowForwardIos fontSize='small' />}>
                  {t('common:button.continue')}
                </Button>
              )}
              <Button variant={isLastStep ? 'contained' : 'outlined'} disableElevation onClick={onSave}>
                {t('common:button.save')}
              </Button>
            </Box>
          </Box>
        }
        dialogContent={
          <Box sx={{ minHeight: '20rem' }}>
            <Stepper activeStep={currentStepIndex} alternativeLabel sx={{ mb: 3, mt: 2 }} connector={<StyledMultiStepConnector />}>
              {STEP_LABELS.map((label, i) => {
                const Icon = STEP_ICONS[i];
                const isActive = currentStepIndex === i;
                return (
                  <Step key={label} onClick={() => goTo(i)} sx={{ cursor: 'pointer' }}>
                    <StepLabel
                      icon={
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: isActive ? 'primary.main' : 'grey.300',
                            color: isActive ? 'white' : 'grey.600',
                          }}
                        >
                          <Icon fontSize='small' />
                        </Box>
                      }
                    >
                      {t(`invoice:accountingIntegration.${label}`)}
                    </StepLabel>
                  </Step>
                );
              })}
            </Stepper>
            <Box sx={{ p: 2 }}>{step}</Box>
          </Box>
        }
      />
      <DeleteConfirmationDialog
        open={confirmDisconnect}
        title={t('invoice:accountingIntegration.disconnectConfirmTitle') as string}
        modaltext={t('invoice:accountingIntegration.disconnectConfirmText') as string}
        value='disconnect'
        confirmButtonText={t('invoice:accountingIntegration.disconnectButton') as string}
        confirmButtonColor='error'
        onClose={(result) => {
          setConfirmDisconnect(false);
          if (result) onDisconnect();
        }}
      />
    </>
  );
};
